import { AppInitState } from '@metaos/hub-sdk';
import type { AccountKey } from 'owa-client-types';
import type { BizChatCompositeKey, WindowKey } from 'owa-bizchat-types';
import { getBizChatCompositeKey, isMainWindowKey } from './getBizChatCompositeKey';

export interface BizChatSessionInfo {
    accountKey: AccountKey;
    windowKey: WindowKey;
    sessionId: string | undefined;
    sessionIdTimestamp: number | undefined;
    appInitState: AppInitState;
    isPrecached: boolean;
    conversationId: string | undefined;
}

const sessionsByKey: Map<BizChatCompositeKey, BizChatSessionInfo> = new Map();

function createDefaultSession(accountKey: AccountKey, windowKey: WindowKey): BizChatSessionInfo {
    return {
        accountKey,
        windowKey,
        sessionId: undefined,
        sessionIdTimestamp: undefined,
        appInitState: AppInitState.Unknown,
        isPrecached: false,
        conversationId: undefined,
    };
}

function getOrCreate(accountKey: AccountKey, windowKey: WindowKey): BizChatSessionInfo {
    const key = getBizChatCompositeKey(accountKey, windowKey);
    let entry = sessionsByKey.get(key);
    if (!entry) {
        entry = createDefaultSession(accountKey, windowKey);
        sessionsByKey.set(key, entry);
    }
    return entry;
}

export function initBizChatSession(accountKey: AccountKey, windowKey: WindowKey): void {
    getOrCreate(accountKey, windowKey);
}

export function setBizChatSessionId(
    accountKey: AccountKey,
    windowKey: WindowKey,
    sessionId: string
): void {
    const entry = getOrCreate(accountKey, windowKey);
    entry.sessionId = sessionId;
    entry.sessionIdTimestamp = Date.now();
}

export function getBizChatSessionId(
    accountKey: AccountKey,
    windowKey: WindowKey
): string | undefined {
    return sessionsByKey.get(getBizChatCompositeKey(accountKey, windowKey))?.sessionId;
}

export function getBizChatSessionState(
    accountKey: AccountKey,
    windowKey: WindowKey
): BizChatSessionInfo | undefined {
    return sessionsByKey.get(getBizChatCompositeKey(accountKey, windowKey));
}

export function getBizChatSessionStateBySessionId(
    sessionId: string
): BizChatSessionInfo | undefined {
    for (const entry of sessionsByKey.values()) {
        if (entry.sessionId === sessionId) {
            return entry;
        }
    }
    return undefined;
}

export function updateBizChatAppInitState(
    accountKey: AccountKey,
    windowKey: WindowKey,
    state: AppInitState
): void {
    const entry = sessionsByKey.get(getBizChatCompositeKey(accountKey, windowKey));
    if (entry) {
        entry.appInitState = state;
    }
}

export function markBizChatPrecached(accountKey: AccountKey, windowKey: WindowKey): void {
    const entry = sessionsByKey.get(getBizChatCompositeKey(accountKey, windowKey));
    if (entry) {
        entry.isPrecached = true;
    }
}

export function clearBizChatPrecached(accountKey: AccountKey, windowKey: WindowKey): void {
    const entry = sessionsByKey.get(getBizChatCompositeKey(accountKey, windowKey));
    if (entry) {
        entry.isPrecached = false;
    }
}

export function setBizChatConversationId(
    accountKey: AccountKey,
    windowKey: WindowKey,
    conversationId: string
): void {
    getOrCreate(accountKey, windowKey).conversationId = conversationId;
}

export function getBizChatConversationId(
    accountKey: AccountKey,
    windowKey: WindowKey
): string | undefined {
    return sessionsByKey.get(getBizChatCompositeKey(accountKey, windowKey))?.conversationId;
}

export function clearBizChatConversationId(accountKey: AccountKey, windowKey: WindowKey): void {
    const entry = sessionsByKey.get(getBizChatCompositeKey(accountKey, windowKey));
    if (entry) {
        entry.conversationId = undefined;
    }
}

export function getBizChatSessionStatus(
    accountKey: AccountKey,
    windowKey: WindowKey
): { isCached: boolean; isPrecached: boolean } {
    const entry = sessionsByKey.get(getBizChatCompositeKey(accountKey, windowKey));
    return {
        isCached: entry?.appInitState === AppInitState.Cached,
        isPrecached: entry?.isPrecached ?? false,
    };
}

export function removeBizChatSession(accountKey: AccountKey, windowKey: WindowKey): void {
    sessionsByKey.delete(getBizChatCompositeKey(accountKey, windowKey));
}

export function clearAllMainWindowBizChatSessions(): void {
    for (const [compositeKey, sessionInfo] of sessionsByKey.entries()) {
        if (isMainWindowKey(sessionInfo.windowKey)) {
            sessionsByKey.delete(compositeKey);
        }
    }
}

export function getAllBizChatSessionIds(): string[] {
    const ids: string[] = [];
    for (const entry of sessionsByKey.values()) {
        if (entry.sessionId !== undefined) {
            ids.push(entry.sessionId);
        }
    }
    return ids;
}

export function getActiveBizChatSessionCounts(): {
    mainCount: number;
    popoutCount: number;
} {
    let mainCount = 0;
    let popoutCount = 0;

    for (const sessionInfo of sessionsByKey.values()) {
        if (sessionInfo.sessionId === undefined) {
            continue;
        }
        if (isMainWindowKey(sessionInfo.windowKey)) {
            mainCount++;
        } else {
            popoutCount++;
        }
    }

    return { mainCount, popoutCount };
}
